"use client";
//css
import styles from "./index.module.scss";
//view
import { Card } from "@douyinfe/semi-ui";
import Image from "next/image";
// ts
import {
  useEffect,
  useRef,
  useState,
  RefObject,
  useCallback,
  useMemo,
} from "react";
import { useTranslations, useCurrentLocale } from "@/app/lib/LocaleProvider";
import { queryCategoryList } from "@/app/api/chatContent";
import { throttle } from "@/app/utils";
import {
  CategoryType,
  TemplateFlagEnum,
  ComponentTypeEnum,
} from "@/app/types/typeConfig/chatQuestion";
import { Spin } from "@douyinfe/semi-ui";
import useReportRequestQueue from "@/app/hooks/robot/useReportRequestQueue";
import Locale from "@/app/locales";
import {
  ReportBizTypeEnum,
  ReportSceneTypeEnum,
  ReportEventEnum,
  ReportContentEnum,
  ReportParentContentEnum,
} from "@/app/types/typeConfig/report";
import { message } from "antd";
import { useMessageStore } from "@/app/store";
import {
  HeaderType,
  MessageSender,
  MessageType,
  MessageStatus,
  RobotMessageType,
  SocketConnectionStatus,
} from "@/app/store/types";
import { createClientUUid, generateUniqueId } from "@/app/utils/string";
import {
  requireCustomerQuestionParams,
  requireRobotQuestionParams,
} from "@/app/utils/requireStoreParams";

import { getQuestionType } from "@/app/api/config";
import { webSocketService } from "@/app/services/WebSocketService";
import { useAuth } from "@/app/lib/AuthProvider";
import { useChatInit } from "@/app/hooks/useChatInit";
interface CategoryListProps {
  language?: string;
  isMobile?: boolean;
}
export default function CategoryList({
  // language,
  isMobile,
  onClickCallback,
}: CategoryListProps & { onClickCallback?: () => void }) {
  const { isAuthenticated, login } = useAuth();
  const { checkCanServiceInfo, checkCanService } = useChatInit();
  const checkCanServiceInfoRef = useRef(checkCanServiceInfo);
  useEffect(() => {
    checkCanServiceInfoRef.current = checkCanServiceInfo;
  }, [checkCanServiceInfo]);
  const t = useTranslations();
  const locale = useCurrentLocale();
  const [categoryList, setCategoryList] = useState<Array<any>>([]);
  const categoryNameRefList = useRef<RefObject<HTMLDivElement>[]>([]);
  const [messageApi, contextHolder] = message.useMessage();
  const { addToReportQueue, requireReportProps } = useReportRequestQueue();
  const { setRobotMessage, addOutgoingMessage, isConnected } =
    useMessageStore();

  // 添加 ref 追踪最后一次请求的 locale，避免重复请求
  const lastRequestedLocaleRef = useRef<string | null>(null);

  useEffect(() => {
    async function requireCategoryList() {
      // 如果已经请求过相同的 locale，跳过
      if (lastRequestedLocaleRef.current === locale) {
        console.log(
          `[CategoryList] Skipping duplicate request for locale: ${locale}`,
        );
        return;
      }

      console.log(`[CategoryList] Fetching data for locale: ${locale}`);
      lastRequestedLocaleRef.current = locale;

      const categoryList = (
        await queryCategoryList({ language: locale })
      ).filter((item) => item.display);
      setCategoryList(categoryList);
    }
    requireCategoryList();
  }, [locale]);

  useEffect(() => {
    console.log("isMobile prop:", isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (categoryNameRefList.current.length) {
      const updatedButtonList = categoryNameRefList.current.map(
        (item, index) => {
          const flag = item.current ? item.current.offsetHeight > 40 : false;
          return { ...categoryList[index], showTooltip: flag };
        },
      );
      setCategoryList((prevList) => {
        if (JSON.stringify(prevList) !== JSON.stringify(updatedButtonList)) {
          return updatedButtonList;
        }
        return prevList;
      });
    }
  }, [categoryList, categoryNameRefList]);

  const throttleClick = useMemo(
    () =>
      throttle(async (item: CategoryType) => {
        if (useMessageStore.getState().isAIStreaming) {
          message.warning(Locale.selectHotAIQuestion.replying);
          return;
        }
        // 先埋点
        addToReportQueue({
          bizType: ReportBizTypeEnum.CATEGORY,
          content: item.categoryName,
          contentId: item.id,
          createTime: new Date().getTime(),
          eventType: ReportEventEnum.CLICK,
          scene: ReportSceneTypeEnum.NORMAL_CONSULT,
          contentType: ReportContentEnum.CATEGORY,
          parentContentType: ReportParentContentEnum.CATEGORY,
        });
        // 外链跳转
        if (item.externalLink) {
          if (
            item.externalLink.includes("/im") ||
            item.externalLink.includes("/liveChat/")
          ) {
            if (isAuthenticated) {
              if (
                isConnected === SocketConnectionStatus.DISCONNECTED ||
                isConnected === SocketConnectionStatus.CLOSE
              ) {
                // 前置检查：实时请求接口判断客服是否在服务时间
                const canProceed = await checkCanService();
                if (!canProceed) {
                  setRobotMessage({
                    type: MessageType.RichText,
                    headerType: HeaderType.MESSAGE,
                    senderType: MessageSender.ROBOT,
                    messageType: RobotMessageType.ROBOT_NORMAL_REPLY,
                    showFeedFooter: false,
                    content: checkCanServiceInfoRef.current?.content || "",
                    timestamp: Date.now(),
                    senderName: "智能助手",
                    robotData: {},
                  });
                  return;
                }
                // 已登录直接出现问题类型选择气泡
                getQuestionType().then((res) => {
                  // 需要选择
                  webSocketService.sendMessage(
                    JSON.stringify({ questionList: res }),
                    {
                      msgType: MessageType.QuestionList,
                      noNeedSendToWebsocket: true,
                      senderType: MessageSender.SYSTEM,
                      msgClassName: "emptyBackground",
                    },
                  );
                });
              } else {
                // 如果已经在会话中，则不进行问题类型选择,提示
                message.info(Locale.chat.hasIMSession);
                return;
              }
            } else {
              login("im");
            }
            // 关闭 popup
            if (onClickCallback) {
              onClickCallback();
            }
            return;
          }
          window.open(item.externalLink, "_blank")?.focus(); // 在新标签页打开链接
          // 关闭 popup
          if (onClickCallback) {
            onClickCallback();
          }
          return;
        }
        if (!item.childrenNode.length && !item.questions?.length) {
          // messageApi.open({
          //   type: "error",
          //   content: Locale.categoryList.error,
          // });
          message.error(Locale.categoryList.error);
          return;
        }
        let title = "";
        if (item.categoryName.indexOf("Out") > -1) {
          title = Locale.carouselImageList.boxIssueTitle;
        } else if (item.categoryName.indexOf("Logistics") > -1) {
          title = Locale.carouselImageList.logisticsIssueTitle;
        }
        const compDirName = item.childrenNode.length
          ? TemplateFlagEnum.SelectCategory
          : TemplateFlagEnum.SelectQuestion;
        const data = item.childrenNode.length
          ? item.childrenNode.filter((item) => item.display)
          : item.questions;

        // Create chat content
        const chatContent = [
          requireCustomerQuestionParams(item.categoryName),
          requireRobotQuestionParams(data, compDirName, {
            type: ComponentTypeEnum.Robot,
            id: item.id,
            title,
            lng: locale,
            parentContentInfo: {
              parentContentId: item.id,
              parentContent: item.categoryName,
              parentContentType: ReportParentContentEnum.CATEGORY,
            },
          }),
        ];

        console.log("CategoryList inserting chat content:", chatContent);

        // Insert into message store
        chatContent.forEach((contentItem) => {
          if (contentItem.type === ComponentTypeEnum.Customer) {
            addOutgoingMessage({
              headerType: HeaderType.MESSAGE,
              clientId: createClientUUid(MessageType.Template),
              content: `{"text":"${contentItem.message || ""}"}`,
              senderType: MessageSender.USER,
              type: MessageType.Text,
              timestamp: Date.now(),
              status: MessageStatus.Success,
            });
          } else {
            setRobotMessage({
              id: contentItem.id || "",
              type: MessageType.Text,
              headerType: HeaderType.MESSAGE,
              senderType: MessageSender.ROBOT,
              messageType: RobotMessageType.ROBOT_ACTION_CARD,
              showFeedFooter: contentItem.showTechAIFooter ?? true,
              content: "",
              timestamp: Date.now(),
              senderName: "智能助手",
              robotData: {
                ...contentItem,
                uniqueKey: contentItem.uniqueKey || generateUniqueId(),
              },
            });
          }
        });
        if (onClickCallback) {
          onClickCallback();
        }
      }, 1000),
    [addToReportQueue, messageApi, t, isConnected, checkCanService],
  );
  const onCategoryClick = useCallback(
    (item: CategoryType) => throttleClick(item),
    [throttleClick],
  );

  return (
    <>
      {categoryList.length === 0 ? (
        <div className={styles.loading}>
          <Spin size="large" />
        </div>
      ) : (
        <div className={styles.categoryList}>
          {categoryList.map((category) => (
            <Card key={category.id} className={styles.categoryItem}>
              <div
                className={styles.itemWrapper}
                onClick={() => onCategoryClick(category)}
              >
                <Image
                  src={category.iconPath}
                  alt={category.categoryName}
                  width={30}
                  height={30}
                />
                <h3 className={styles.categoryName}>{category.categoryName}</h3>
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
